import React, { Fragment, useContext, useEffect } from 'react'
import { Box, Typography, Stack, TextField, MenuItem, Grid } from '@mui/material'
import { Maybe } from 'graphql/jsutils/Maybe'
import { SelectionField, User, UserRole } from '../../../generated'
import { AuthContext } from '../../context/AuthContext'
import { isAttorneyScopedUser, isParalegalScopedUser } from '../../utils'
import ClientFilter from './filters/ClientFilter'
import EnumFilter from './filters/EnumFilter'
import { SORT_DIR_TYPE } from '../../constants'
import { availableEnumOptions, isSelectionFieldVisible } from '../../utils/reports'
import DatePicker from './components/DatePicker'
import DateRangePicker from './components/DateRangePicker'
import OfficeFilter from './filters/OfficeFilter'
import LocationFilter from './filters/LocationFilter'
import MatterFilter from './filters/MatterFilter'
import LawTypeFilter from './filters/LawTypeFilter'
import ReferralTypeFilter from './filters/ReferralTypeFilter'
import ReferralSourceFilter from './filters/ReferenceFilter'
import BankAccountFilter from './filters/BankAccountFilter'
import MatterMultiSelectFilter from './filters/MatterMultiSelectFilter'
import VendorFilter from './filters/VendorFilter'
import CourtFilter from './filters/CourtFilter'
import GlAccountFilter from './filters/GlAccountFilter'
import AttorneyDropdown from '../common/AttorneyDropdown'

const ALL_USER_ROLES: UserRole[] = [
  UserRole.Attorney,
  UserRole.Paralegal,
  UserRole.Investigator,
  UserRole.Accountant,
  UserRole.Staff
]

interface ReportCustomizationProps {
  /** The selected report's key, e.g. `trialBalance`; some filters narrow their options per report. */
  reportSelection?: string
  selectionFields: (SelectionField | null)[]
  reportFilters: Record<string, any>
  sortFields: (string | null)[]
  setReportFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>
}

export const ReportCustomization: React.FC<ReportCustomizationProps> = ({
  reportSelection,
  selectionFields,
  sortFields,
  reportFilters,
  setReportFilters
}) => {
  const { currentUser } = useContext(AuthContext)
  const isAttorneyScoped = isAttorneyScopedUser(currentUser)
  const isParalegalScoped = isParalegalScopedUser(currentUser)

  // The current user's own name, read-only, for a filter they are locked to (attorney Resp 1,
  // paralegal Paralegal). The value itself is seeded in ReportWidget and enforced again by the API.
  const renderOwnNameField = (field: SelectionField, fallbackLabel: string) => (
    <TextField
      key={field?.field}
      label={field?.label || fallbackLabel}
      value={`${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim()}
      InputProps={{ readOnly: true }}
      disabled
      sx={{ minWidth: 250 }}
    />
  )

  // A Compare With choice the period no longer offers, e.g. "full last year" kept after the year was
  // changed to 2026, falls back to the default so a hidden option is never submitted.
  useEffect(() => {
    const compareWith = selectionFields.find(field => field?.field === 'compareWith')
    const selected = reportFilters?.compareWith
    if (!compareWith || !selected) return

    if (!availableEnumOptions(reportSelection, compareWith, reportFilters).includes(selected)) {
      setReportFilters(prev => ({ ...prev, compareWith: compareWith.defaultValue ?? undefined }))
    }
  }, [reportSelection, selectionFields, reportFilters, setReportFilters])

  const handleSortFieldChange = (rowIndex: number, value: string) => {
    setReportFilters(prev => {
      const sortArray = [...(prev.sortFields ?? [])]

      // Replace or insert at index
      if (value) {
        sortArray[rowIndex] = {
          field: value,
          direction: sortArray[rowIndex]?.direction || 'ASC'
        }
      } else {
        sortArray.splice(rowIndex, 1)
      }

      return { ...prev, sortFields: sortArray }
    })
  }

  const handleSortDirectionChange = (rowIndex: number, value: SORT_DIR_TYPE) => {
    setReportFilters(prev => {
      const sortArray = [...(prev.sortFields ?? [])]
      if (sortArray[rowIndex]) {
        sortArray[rowIndex] = { ...sortArray[rowIndex], direction: value }
      }
      return { ...prev, sortFields: sortArray }
    })
  }

  const renderTextField = (field: SelectionField) => {
    switch (field?.field) {
      case 'referenceId':
        return (
          <ReferralSourceFilter
            key={field?.field}
            referenceId={reportFilters?.referenceId}
            setReportFilters={setReportFilters}
          />
        )
      case 'clientId':
        return (
          <ClientFilter key={field?.field} clientId={reportFilters?.clientId} setReportFilters={setReportFilters} />
        )

      case 'matterId':
        return (
          <MatterFilter
            key={field?.field}
            matterId={reportFilters?.matterId}
            setReportFilters={setReportFilters}
            userId={isAttorneyScoped ? currentUser?.id : undefined}
          />
        )

      case 'bankAccountId':
        return (
          <BankAccountFilter
            key={field?.field}
            bankAccountId={reportFilters?.bankAccountId}
            setReportFilters={setReportFilters}
          />
        )

      case 'matterIds':
        return (
          <MatterMultiSelectFilter
            key={field?.field}
            matterIds={reportFilters?.matterIds}
            setReportFilters={setReportFilters}
          />
        )

      case 'vendorId':
        return (
          <VendorFilter key={field?.field} vendorId={reportFilters?.vendorId} setReportFilters={setReportFilters} />
        )

      case 'courtId':
        return (
          <CourtFilter key={field?.field} courtId={reportFilters?.courtId} setReportFilters={setReportFilters} />
        )

      case 'glAccountId':
        return (
          <GlAccountFilter
            key={field?.field}
            glAccountId={reportFilters?.glAccountId}
            setReportFilters={setReportFilters}
          />
        )

      case 'lawTypeId':
        return (
          <LawTypeFilter key={field?.field} lawTypeId={reportFilters?.lawTypeId} setReportFilters={setReportFilters} />
        )

      case 'referralType':
        return <ReferralTypeFilter key={field?.field} setReportFilters={setReportFilters} />

      case 'user':
        return (
          <AttorneyDropdown
            key={field?.field}
            selectedAttorneyId={reportFilters?.user}
            onChange={(value: Maybe<User>) => setReportFilters((prev: any) => ({ ...prev, user: value?.id }))}
            label={field?.label || 'User'}
            sx={{ minWidth: 250 }}
            roles={ALL_USER_ROLES}
          />
        )

      case 'originatingAttorney':
        return (
          <AttorneyDropdown
            key={field?.field}
            selectedAttorneyId={reportFilters?.originatingAttorney}
            onChange={(value: Maybe<User>) =>
              setReportFilters((prev: any) => ({ ...prev, originatingAttorney: value?.id }))
            }
            label={field?.label || 'Originating Attorney'}
            sx={{ minWidth: 250 }}
            roles={[UserRole.Attorney]}
          />
        )

      case 'responsibleAttorney1':
        // An attorney may only run these reports for themselves, so the picker is replaced by a
        // read-only field rather than a disabled AttorneyDropdown: that component resolves its
        // displayed value from the currently loaded page of users (or its internal "picked"
        // pin), so a value pushed in from outside routinely renders blank. The value itself is
        // seeded in ReportWidget and enforced again by the API.
        if (isAttorneyScoped) return renderOwnNameField(field, 'Resp 1')

        return (
          <AttorneyDropdown
            key={field?.field}
            selectedAttorneyId={reportFilters?.responsibleAttorney1}
            onChange={(value: Maybe<User>) =>
              setReportFilters((prev: any) => ({ ...prev, responsibleAttorney1: value?.id }))
            }
            label={field?.label || 'Resp 1'}
            sx={{ minWidth: 250 }}
            // Resp 1 is always an attorney; listing support staff here only offered filters that
            // match no active matters. Also used by Matter List by Attorney and Active Matters Recap.
            roles={[UserRole.Attorney]}
          />
        )

      // HCK-274: Matter List by Attorney / Paralegal — the matter's Paralegal slot, so paralegals only.
      // HCK-292: a paralegal runs it only for themselves, locked the same way as an attorney's Resp 1.
      case 'paralegal':
        if (isParalegalScoped) return renderOwnNameField(field, 'Paralegal')

        return (
          <AttorneyDropdown
            key={field?.field}
            selectedAttorneyId={reportFilters?.paralegal}
            onChange={(value: Maybe<User>) => setReportFilters((prev: any) => ({ ...prev, paralegal: value?.id }))}
            label={field?.label || 'Paralegal'}
            sx={{ minWidth: 250 }}
            roles={[UserRole.Paralegal]}
          />
        )

      case 'noBillSinceDays': {
        const current = reportFilters?.noBillSinceDays ?? 30
        return (
          <TextField
            key={field?.field}
            type="number"
            label={field?.label || 'No Bill Since (Days)'}
            name={field?.field}
            value={current}
            onChange={e => {
              const raw = e.target.value
              const next = raw === '' ? '' : Math.min(1000, Math.max(0, Math.floor(Number(raw))))
              setReportFilters(prev => ({ ...prev, noBillSinceDays: next }))
            }}
            inputProps={{ min: 0, max: 1000, step: 1 }}
            sx={{ minWidth: 250 }}
          />
        )
      }

      // BL5 thresholds. The API clamps both again, so these bounds are a convenience, not the
      // guard. A cleared input snaps back to the template default on blur, so the value the report
      // runs with is always the one on screen and in Report History.
      case 'maxOutstandingAmount':
      case 'minBillAgeDays': {
        const isAmount = field.field === 'maxOutstandingAmount'
        const fallback = field?.defaultValue ?? (isAmount ? 15 : 90)
        const current = reportFilters?.[field.field] ?? fallback
        return (
          <TextField
            key={field?.field}
            type="number"
            label={field?.label || ''}
            name={field?.field}
            value={current}
            onChange={e => {
              const raw = e.target.value
              // Amounts keep the raw string while typing so "0." / "12.5" are not mangled mid-entry;
              // days are whole numbers, clamped like BL3's No Bill Since (Days).
              const next =
                raw === '' || isAmount ? raw : Math.min(3650, Math.max(0, Math.floor(Number(raw))))
              setReportFilters(prev => ({ ...prev, [field.field]: next }))
            }}
            onBlur={() => {
              if (reportFilters?.[field.field] === '') {
                setReportFilters(prev => ({ ...prev, [field.field]: fallback }))
              }
            }}
            inputProps={isAmount ? { min: 0.01, max: 100000, step: 0.01 } : { min: 0, max: 3650, step: 1 }}
            sx={{ minWidth: 250 }}
          />
        )
      }

      default:
        return (
          <TextField
            key={field?.field}
            label={field?.label || ''}
            placeholder={field?.label || ''}
            name={field?.field}
            value={reportFilters?.[field?.field]}
            onChange={e => setReportFilters(prev => ({ ...prev, [field?.field]: e.target.value }))}
            sx={{ minWidth: 250 }}
          />
        )
    }
  }

  const renderSelectionFields = () => {
    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 2, pb: 2, display: 'block' }}>
          Filter By:
        </Typography>
        <Stack direction="row" sx={{ gap: 2, alignItems: 'end', marginTop: '0px !important', flexWrap: 'wrap' }}>
          {selectionFields.map(field => {
            if (!field) return null

            if (!isSelectionFieldVisible(field, reportFilters)) {
              return null
            }

            switch (field?.type) {
              case 'STRING':
                return renderTextField(field)

              case 'ENUM':
                return (
                  <EnumFilter
                    key={field?.field}
                    selectedOption={reportFilters?.[field?.field]}
                    setReportFilters={setReportFilters}
                    field={{ ...field, options: availableEnumOptions(reportSelection, field, reportFilters) }}
                  />
                )

              case 'DATE':
                return (
                  <DatePicker
                    key={field?.field}
                    field={field}
                    reportFilters={reportFilters}
                    setReportFilters={setReportFilters}
                  />
                )

              case 'DATE_RANGE':
                return (
                  <DateRangePicker
                    key={field?.field}
                    field={field}
                    reportFilters={reportFilters}
                    setReportFilters={setReportFilters}
                  />
                )

              case 'BOOLEAN':
                return null

              case 'NUMBER':
                if (field?.field === 'profitCenter') {
                  return (
                    <OfficeFilter
                      key={field?.field}
                      officeName={reportFilters?.profitCenter}
                      setReportFilters={setReportFilters}
                      isProfitCenter={true}
                    />
                  )
                }
                if (field?.field === 'location') {
                  return (
                    <LocationFilter
                      key={field?.field}
                      locationName={reportFilters?.location}
                      setReportFilters={setReportFilters}
                    />
                  )
                }
                return null

              default:
                return null
            }
          })}
        </Stack>
      </>
    )
  }

  const renderSortFields = () => {
    return (
      <Fragment>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, display: 'block' }}>
          Sort By:
        </Typography>

        <Grid container gap={3}>
          {sortFields.map((_, index) => {
            const sortArray = reportFilters?.sortFields ?? []
            const selectedField = sortArray[index]?.field || ''
            const selectedDirection = sortArray[index]?.direction || 'ASC'

            // List of fields already selected in other rows
            const selectedKeys = (sortArray.filter(Boolean) as any[]).map(s => s.field)
            const fieldOptions = sortFields.filter(f => !selectedKeys.includes(f) || f === selectedField)
            // Disable if previous field is not selected
            const disabled = index > 0 && !selectedKeys[index - 1]

            return (
              <Grid item xs={4} key={index}>
                <Stack direction="row">
                  <TextField
                    sx={{ flex: 1, marginRight: 2 }}
                    select
                    label="Sort Field"
                    value={selectedField}
                    disabled={disabled}
                    onChange={e => handleSortFieldChange(index, e.target.value)}
                  >
                    <MenuItem value="">--Select--</MenuItem>
                    {fieldOptions.map(
                      f =>
                        f && (
                          <MenuItem key={f} value={f}>
                            {f}
                          </MenuItem>
                        )
                    )}
                  </TextField>

                  <TextField
                    select
                    label="A/D"
                    value={selectedDirection}
                    onChange={e => handleSortDirectionChange(index, e.target.value as SORT_DIR_TYPE)}
                    disabled={!selectedField}
                    sx={{ width: '100px' }}
                  >
                    <MenuItem value="ASC">ASC</MenuItem>
                    <MenuItem value="DESC">DESC</MenuItem>
                  </TextField>
                </Stack>
              </Grid>
            )
          })}
        </Grid>
      </Fragment>
    )
  }

  if (!selectionFields?.length) {
    return null
  }

  return (
    <Box sx={{ marginTop: '0px !important' }} p={2} pt={0} pb={3}>
      <Typography
        variant="subtitle1"
        fontSize={18}
        fontWeight={700}
        color={'#000000'}
        sx={{ mb: 2, marginTop: '0px !important' }}
      >
        Report Customization
      </Typography>
      <Stack spacing={3}>
        {renderSelectionFields()}
        {!!sortFields.length && renderSortFields()}
      </Stack>
    </Box>
  )
}
